import axios from 'axios';

const API_URL = process.env.VUE_APP_API_URL;

class Signal {
  async list(offset = 0, limit = 20, { sort, pinned, search, sources } = {}) {
    try {
      const params = new URLSearchParams({
        offset: offset.toString(),
        limit: limit.toString(),
      });
      if (sort) params.set('sort', sort);
      if (pinned) params.set('pinned', 'true');
      if (search) params.set('search', search);
      if (sources?.length) params.set('sources', sources.join(','));
      const { data } = await axios.get(`${API_URL}/signals?${params.toString()}`);
      return { status: "success", data: data.data };
    } catch(error) {
      if (error.response?.status == 429) {
        return { status: "ratelimited", message: "Too many requests" }
      } else if (error.response?.status == 401) {
        return { status: "unauthorized", message: "Not authorized" }
      } else if (error.response?.status == 500) {
        return { status: "error", message: "Server error occurred" }
      } else {
        return { status: "error", message: "An error occurred" }
      }
    }
  }

  async get(id, { sources } = {}) {
    try {
      const params = new URLSearchParams();
      if (sources?.length) params.set('sources', sources.join(','));
      const qs = params.toString() ? `?${params.toString()}` : '';
      const { data } = await axios.get(`${API_URL}/signals/${id}${qs}`);
      return { status: "success", data: data.data, saved_search: data.saved_search };
    } catch(error) {
      if (error.response?.status == 429) {
        return { status: "ratelimited", message: "Too many requests" }
      } else if (error.response?.status == 401) {
        return { status: "unauthorized", message: "Not authorized" }
      } else if (error.response?.status == 404) {
        return { status: "not_found", message: "Signal not found" }
      } else if (error.response?.status == 500) {
        return { status: "error", message: "Server error occurred" }
      } else {
        return { status: "error", message: "An error occurred" }
      }
    }
  }

  async createFromURL(sourceUrl, skipVerification = false) {
    try {
      const body = { source_url: sourceUrl };
      if (skipVerification) body.skip_verification = true;
      const { data } = await axios.post(`${API_URL}/admin/signals`, body);
      return { status: "success", data: data.data };
    } catch(error) {
      if (error.response?.status == 429) {
        return { status: "ratelimited", message: "Too many requests" }
      } else if (error.response?.status == 401) {
        return { status: "unauthorized", message: "Not authorized" }
      } else if (error.response?.status == 400) {
        return { status: "validation_error", message: error.response.data.message || "Invalid data" }
      } else if (error.response?.status == 500) {
        return { status: "error", message: "Server error occurred" }
      } else {
        return { status: "error", message: "An error occurred" }
      }
    }
  }

  async createFromPDF(file) {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await axios.post(`${API_URL}/admin/signals`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000,
      });
      return { status: "success", data: data.data };
    } catch(error) {
      if (error.response?.status == 429) {
        return { status: "ratelimited", message: "Too many requests" }
      } else if (error.response?.status == 401) {
        return { status: "unauthorized", message: "Not authorized" }
      } else if (error.response?.status == 400) {
        return { status: "validation_error", message: error.response.data.message || "Invalid file" }
      } else if (error.response?.status == 500) {
        return { status: "error", message: "Server error occurred" }
      } else {
        return { status: "error", message: "An error occurred" }
      }
    }
  }

  async pin(id) {
    try {
      await axios.post(`${API_URL}/signals/${id}/pin`);
      return { status: "success" };
    } catch(error) {
      if (error.response?.status == 429) {
        return { status: "ratelimited", message: "Too many requests" }
      } else if (error.response?.status == 401) {
        return { status: "unauthorized", message: "Not authorized" }
      } else {
        return { status: "error", message: "An error occurred" }
      }
    }
  }

  async unpin(id) {
    try {
      await axios.delete(`${API_URL}/signals/${id}/pin`);
      return { status: "success" };
    } catch(error) {
      if (error.response?.status == 429) {
        return { status: "ratelimited", message: "Too many requests" }
      } else if (error.response?.status == 401) {
        return { status: "unauthorized", message: "Not authorized" }
      } else {
        return { status: "error", message: "An error occurred" }
      }
    }
  }

  // Feed management (admin)
  async listFeeds() {
    try {
      const { data } = await axios.get(`${API_URL}/admin/signals/feeds`);
      return { status: "success", data: data.data };
    } catch(error) {
      if (error.response?.status == 401) {
        return { status: "unauthorized", message: "Not authorized" }
      } else {
        return { status: "error", message: "An error occurred" }
      }
    }
  }

  async createFeed(url, title) {
    try {
      const body = { url };
      if (title) body.title = title;
      const { data } = await axios.post(`${API_URL}/admin/signals/feeds`, body);
      return { status: "success", data: data.data };
    } catch(error) {
      if (error.response?.status == 409) {
        return { status: "conflict", message: error.response.data.message || "Feed already exists" }
      } else if (error.response?.status == 400) {
        return { status: "validation_error", message: error.response.data.message || "Invalid feed URL" }
      } else if (error.response?.status == 401) {
        return { status: "unauthorized", message: "Not authorized" }
      } else {
        return { status: "error", message: "An error occurred" }
      }
    }
  }

  async updateFeed(id, { enabled }) {
    try {
      await axios.put(`${API_URL}/admin/signals/feeds/${id}`, { enabled });
      return { status: "success" };
    } catch(error) {
      if (error.response?.status == 401) {
        return { status: "unauthorized", message: "Not authorized" }
      } else {
        return { status: "error", message: "An error occurred" }
      }
    }
  }

  async deleteFeed(id) {
    try {
      await axios.delete(`${API_URL}/admin/signals/feeds/${id}`);
      return { status: "success" };
    } catch(error) {
      if (error.response?.status == 401) {
        return { status: "unauthorized", message: "Not authorized" }
      } else if (error.response?.status == 404) {
        return { status: "not_found", message: "Feed not found" }
      } else {
        return { status: "error", message: "An error occurred" }
      }
    }
  }

  // Feed suggestions (user)
  async suggestFeed(url) {
    try {
      const { data } = await axios.post(`${API_URL}/signals/feeds/suggest`, { url });
      return { status: "success", data: data.data };
    } catch(error) {
      if (error.response?.status == 409) {
        return { status: "conflict", message: error.response.data.message || "Feed already exists" }
      } else if (error.response?.status == 400) {
        return { status: "validation_error", message: error.response.data.message || "Invalid feed URL" }
      } else if (error.response?.status == 401) {
        return { status: "unauthorized", message: "Not authorized" }
      } else {
        return { status: "error", message: "An error occurred" }
      }
    }
  }

  async listAdmin(offset = 0, limit = 20, { status, search } = {}) {
    try {
      const params = new URLSearchParams({
        offset: offset.toString(),
        limit: limit.toString(),
      });
      if (status !== undefined && status !== null) params.set('status', status.toString());
      if (search) params.set('search', search);
      const { data } = await axios.get(`${API_URL}/admin/signals?${params.toString()}`);
      return { status: "success", data: data.data };
    } catch(error) {
      if (error.response?.status == 401) {
        return { status: "unauthorized", message: "Not authorized" }
      } else {
        return { status: "error", message: "An error occurred" }
      }
    }
  }

  async edit(id, method, data) {
    try {
      await axios.put(`${API_URL}/admin/signals/${id}`, { method, data });
      return { status: "success" };
    } catch(error) {
      if (error.response?.status == 401) {
        return { status: "unauthorized", message: "Not authorized" }
      } else if (error.response?.status == 404) {
        return { status: "not_found", message: error.response.data?.message || "Not found" }
      } else if (error.response?.status == 400) {
        return { status: "validation_error", message: error.response.data?.message || "Invalid request" }
      } else {
        return { status: "error", message: "An error occurred" }
      }
    }
  }

  async markIrrelevant(id) {
    return this.edit(id, 'update_status', { status: 4 });
  }

  async retry(id, { clearError = false } = {}) {
    if (clearError) {
      const res = await this.edit(id, 'clear_error', {})
      if (res.status !== 'success') return res
    }
    return this.edit(id, 'update_status', { status: 0 })
  }

  async delete(id) {
    try {
      await axios.delete(`${API_URL}/admin/signals/${id}`);
      return { status: "success" };
    } catch(error) {
      if (error.response?.status == 429) {
        return { status: "ratelimited", message: "Too many requests" }
      } else if (error.response?.status == 401) {
        return { status: "unauthorized", message: "Not authorized" }
      } else if (error.response?.status == 404) {
        return { status: "not_found", message: "Signal not found" }
      } else if (error.response?.status == 500) {
        return { status: "error", message: "Server error occurred" }
      } else {
        return { status: "error", message: "An error occurred" }
      }
    }
  }
}

export default new Signal();
